// ═══════════════════════════════════════════════════════════════
// CAMPAIGN SERVICE — Supabase CRUD for campaigns + campaign_recipients
// Path: src/services/campaignService.js
//
// Tracks direct mail campaigns: who received them, who responded.
// Follows eventService.js patterns.
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

const PAGE_SIZE = 1000;

export const campaignService = {
  // ─── Campaign CRUD ──────────────────────────────────────────

  async getAll() {
    const { data, error } = await supabase
      .from("campaigns")
      .select("*")
      .order("date_sent", { ascending: false });

    if (error) {
      debugLog.error("campaignService.getAll", error);
      throw error;
    }
    return data || [];
  },

  async getById(campaignId) {
    const { data, error } = await supabase
      .from("campaigns")
      .select("*")
      .eq("id", campaignId)
      .single();

    if (error) {
      debugLog.error("campaignService.getById", error);
      throw error;
    }
    return data;
  },

  async create(campaignData) {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from("campaigns")
      .insert({
        name: campaignData.name,
        date_sent: campaignData.dateSent || null,
        segment_description: campaignData.segmentDescription || null,
        status: campaignData.status || "active",
        created_by: user?.id || null,
      })
      .select()
      .single();

    if (error) {
      debugLog.error("campaignService.create", error);
      throw error;
    }
    debugLog.action("Campaign created", { name: campaignData.name });
    return data;
  },

  async update(campaignId, updates) {
    const payload = { updated_at: new Date().toISOString() };
    if (updates.name !== undefined) payload.name = updates.name;
    if (updates.dateSent !== undefined) payload.date_sent = updates.dateSent;
    if (updates.segmentDescription !== undefined)
      payload.segment_description = updates.segmentDescription;
    if (updates.status !== undefined) payload.status = updates.status;

    const { data, error } = await supabase
      .from("campaigns")
      .update(payload)
      .eq("id", campaignId)
      .select()
      .single();

    if (error) {
      debugLog.error("campaignService.update", error);
      throw error;
    }
    debugLog.action("Campaign updated", { campaignId });
    return data;
  },

  async remove(campaignId) {
    const { error } = await supabase
      .from("campaigns")
      .delete()
      .eq("id", campaignId);

    if (error) {
      debugLog.error("campaignService.remove", error);
      throw error;
    }
    debugLog.action("Campaign deleted", { campaignId });
  },

  // ─── Recipients ─────────────────────────────────────────────

  async getRecipients(campaignId) {
    const allRows = [];
    let offset = 0;

    while (true) {
      const { data, error } = await supabase
        .from("campaign_recipients")
        .select(
          "id, campaign_id, member_id, match_method, journey_stage, created_at, members (id, first_name, last_name, credentialed_full_name, spektrix_id, email, home_city, home_state)"
        )
        .eq("campaign_id", campaignId)
        .order("created_at", { ascending: true })
        .range(offset, offset + PAGE_SIZE - 1);

      if (error) {
        debugLog.error("campaignService.getRecipients", error);
        throw error;
      }
      if (!data) break;
      allRows.push(...data);
      if (data.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    return allRows;
  },

  async addRecipients(campaignId, memberIds, matchMethod = "manual") {
    const CHUNK = 500;
    let inserted = 0;

    for (let i = 0; i < memberIds.length; i += CHUNK) {
      const chunk = memberIds.slice(i, i + CHUNK);
      const rows = chunk.map((memberId) => ({
        campaign_id: campaignId,
        member_id: memberId,
        match_method: matchMethod,
      }));

      const { error } = await supabase
        .from("campaign_recipients")
        .upsert(rows, { onConflict: "campaign_id,member_id", ignoreDuplicates: true });

      if (error) {
        debugLog.error("campaignService.addRecipients chunk", error);
      } else {
        inserted += chunk.length;
      }
    }

    debugLog.action("Campaign recipients added", { campaignId, count: inserted });
    return inserted;
  },

  async removeRecipient(recipientId) {
    const { error } = await supabase
      .from("campaign_recipients")
      .delete()
      .eq("id", recipientId);

    if (error) {
      debugLog.error("campaignService.removeRecipient", error);
      throw error;
    }
  },

  // ─── Journey Stage ─────────────────────────────────────────

  async updateJourneyStage(recipientId, stage) {
    const { data, error } = await supabase
      .from("campaign_recipients")
      .update({ journey_stage: stage })
      .eq("id", recipientId)
      .select("id, journey_stage")
      .single();

    if (error) {
      debugLog.error("campaignService.updateJourneyStage", error);
      throw error;
    }
    debugLog.action("Journey stage updated", { recipientId, stage });
    return data;
  },

  async bulkUpdateJourneyStage(recipientIds, stage) {
    if (!recipientIds?.length) return;
    const CHUNK = 500;
    for (let i = 0; i < recipientIds.length; i += CHUNK) {
      const chunk = recipientIds.slice(i, i + CHUNK);
      const { error } = await supabase
        .from("campaign_recipients")
        .update({ journey_stage: stage })
        .in("id", chunk);

      if (error) {
        debugLog.error("campaignService.bulkUpdateJourneyStage", error);
        throw error;
      }
    }
    debugLog.action("Journey stages bulk updated", {
      count: recipientIds.length,
      stage,
    });
  },

  async advanceJourneyOnGift(memberId, campaignId) {
    // Find the campaign_recipient row
    const { data: recipient, error: errR } = await supabase
      .from("campaign_recipients")
      .select("id, journey_stage")
      .eq("campaign_id", campaignId)
      .eq("member_id", memberId)
      .maybeSingle();

    if (errR || !recipient) return null;

    // Already at donor_of_record — nothing to do
    if (recipient.journey_stage === "donor_of_record") return recipient;

    // Count total gifts for this member in this campaign
    const { count, error: errG } = await supabase
      .from("gifts")
      .select("id", { count: "exact", head: true })
      .eq("member_id", memberId)
      .eq("campaign_id", campaignId);

    if (errG) {
      debugLog.error("campaignService.advanceJourneyOnGift count", errG);
      return null;
    }

    let newStage = recipient.journey_stage;
    if (count >= 2) {
      newStage = "donor_of_record";
    } else if (count >= 1) {
      newStage = "first_gift";
    }

    if (newStage !== recipient.journey_stage) {
      return await this.updateJourneyStage(recipient.id, newStage);
    }
    return recipient;
  },

  // ─── Stats ──────────────────────────────────────────────────

  async getCampaignStats(campaignId) {
    // Get recipients with journey stage
    const { data: recipients, error: errR } = await supabase
      .from("campaign_recipients")
      .select("member_id, journey_stage")
      .eq("campaign_id", campaignId);

    if (errR) {
      debugLog.error("campaignService.getCampaignStats recipients", errR);
      return {
        recipientCount: 0, responseCount: 0, totalRaised: 0, responseRate: 0,
        stageCounts: { imported: 0, card_sent: 0, first_gift: 0, donor_of_record: 0 },
      };
    }

    const recipientCount = (recipients || []).length;

    // Journey stage breakdown
    const stageCounts = { imported: 0, card_sent: 0, first_gift: 0, donor_of_record: 0 };
    (recipients || []).forEach((r) => {
      const stage = r.journey_stage || "imported";
      if (stageCounts[stage] !== undefined) stageCounts[stage]++;
    });

    if (recipientCount === 0) {
      return {
        recipientCount: 0, responseCount: 0, totalRaised: 0, responseRate: 0,
        stageCounts,
      };
    }

    // Get gifts linked to this campaign
    const { data: gifts, error: errG } = await supabase
      .from("gifts")
      .select("member_id, amount")
      .eq("campaign_id", campaignId);

    if (errG) {
      debugLog.error("campaignService.getCampaignStats gifts", errG);
      return { recipientCount, responseCount: 0, totalRaised: 0, responseRate: 0, stageCounts };
    }

    const respondedMembers = new Set((gifts || []).map((g) => g.member_id));
    const totalRaised = (gifts || []).reduce((sum, g) => sum + (Number(g.amount) || 0), 0);

    return {
      recipientCount,
      responseCount: respondedMembers.size,
      totalRaised,
      responseRate: recipientCount > 0 ? Math.round((respondedMembers.size / recipientCount) * 100) : 0,
      stageCounts,
    };
  },

  // ─── Per-Member Campaign Lookup ─────────────────────────────

  async getCampaignsByMember(memberId) {
    const allRows = [];
    let offset = 0;

    while (true) {
      const { data, error } = await supabase
        .from("campaign_recipients")
        .select(
          "id, campaign_id, match_method, created_at, campaigns (id, name, date_sent, segment_description, status)"
        )
        .eq("member_id", memberId)
        .order("created_at", { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);

      if (error) {
        debugLog.error("campaignService.getCampaignsByMember", error);
        throw error;
      }
      if (!data) break;
      allRows.push(...data);
      if (data.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    return allRows;
  },

  // ─── Dropdown (for gift entry) ──────────────────────────────

  async getForDropdown() {
    const { data, error } = await supabase
      .from("campaigns")
      .select("id, name, date_sent")
      .neq("status", "closed")
      .order("date_sent", { ascending: false })
      .limit(50);

    if (error) {
      debugLog.error("campaignService.getForDropdown", error);
      return [];
    }
    return data || [];
  },

  // ─── CSV Matching ───────────────────────────────────────────

  /**
   * Match CSV rows to members.
   * @param {Array} rows — parsed CSV rows (array of objects keyed by header)
   * @param {Object} columnMapping — { csvColumn: memberField } for matching
   * @param {string} strategy — 'spektrix_id' or 'composite'
   * @param {Array} members — the members array from AppContext
   * @returns {{ matched: [{row, memberId}], unmatched: [{row}] }}
   */
  matchRecipients(rows, columnMapping, strategy, members) {
    const matched = [];
    const unmatched = [];

    if (strategy === "spektrix_id") {
      const csvColumn = Object.keys(columnMapping)[0];
      // Build lookup: spektrix_id → member.id
      const lookup = {};
      members.forEach((m) => {
        if (m.spektrixId) lookup[String(m.spektrixId).trim().toLowerCase()] = m.id;
      });

      rows.forEach((row) => {
        const val = String(row[csvColumn] || "").trim().toLowerCase();
        if (val && lookup[val]) {
          matched.push({ row, memberId: lookup[val] });
        } else {
          unmatched.push({ row });
        }
      });
    } else {
      // Composite key matching
      // Build key from member fields
      const memberKeys = {};
      members.forEach((m) => {
        const parts = Object.values(columnMapping).map((field) => {
          const val = m[field] || "";
          return String(val).trim().toLowerCase();
        });
        const key = parts.join("|");
        if (key && key !== "|".repeat(parts.length - 1)) {
          memberKeys[key] = m.id;
        }
      });

      rows.forEach((row) => {
        const parts = Object.keys(columnMapping).map((csvCol) => {
          const val = row[csvCol] || "";
          return String(val).trim().toLowerCase();
        });
        const key = parts.join("|");
        if (key && memberKeys[key]) {
          matched.push({ row, memberId: memberKeys[key] });
        } else {
          unmatched.push({ row });
        }
      });
    }

    return { matched, unmatched };
  },
};
